import React, { useMemo, useState } from 'react'
import { makeStyles } from '@mui/styles'
import orgamax from 'ampersand-app'

import DocumentType from '@/enums/document-type.enum'

import { updateSharedState } from '@/redux/ducks/document/list'
import { saveDocument } from '@/redux/ducks/document/documentDrawerHelpers'
import { closeDocumentDrawer, fetchDocumentDetails, openPaymentDrawer, requestReviewReload, updateDocument } from '@/redux/ducks/document/drawer'
// The TYPED dispatch, not react-redux's bare `useDispatch`: the untyped one rejects a
// thunk (AsyncThunkAction is not AnyAction), which is why every fetchDocumentDetails
// call in this file carried a TS2345. Runtime-identical — useAppDispatch IS useDispatch.
import { useAppDispatch, useAppSelector } from '@/redux/store'

import { USER_RIGHTS, ProductTypes, SUBSCRIPTION_MODULES, mimeTypes, experimentalFeatures } from '@/modules/base/util/constants'
import { EXTERNAL_OWNER_SYSTEMS, TAG_TYPES } from '@/modules/file/constants'
import useCheckModuleAccess from '@/modules/_helpers/accessCheck/useCheckModuleAccess'
import { ButtonIconSquare } from '@/modules/base/components/buttons'
import { TagType } from '@/modules/document/types'
import SendEmailDrawer from '@/modules/document/components/edit/send-email-drawer'
import FileService from '@/modules/file/fileService'
import { toast } from '@/modules/base/components/toast/toastConfig'

import lang from '@/lang'
import IconTodo from '@assets/images/icons/to-do.svg'
import IconScan from '@assets/images/icons/ocr.svg'
import IconSign from '@assets/images/icons/signature_pen.svg'
import IconQrPay from '@assets/images/icons/scan.svg'
import IconEmail from '@assets/images/icons/email.svg'
import IconUnshare from '@assets/images/icons/cancel_sharing.svg'
import IconCopyLink from '@assets/images/icons/copy_link.svg'
import IconShare from '@assets/images/icons/share.svg'
import IconBank from '@assets/images/icons/finances.svg'
import IconAdd from '@assets/images/icons/add.svg'
import IconReset from '@assets/images/icons/reset.svg'
import useCheckAccessRights from '@/modules/_helpers/accessCheck/useCheckAccessRights'
import ThemeInterface from '@/theme-interface'
import { includes } from 'lodash'
import { CssHelper } from '@/modules/base/view/mixin/helpers_mixin'
import CascadingMenuButton, { CascadingContextMenuItem } from '@/modules/base/components/cascading-context-menu/cascading-menu-button'
import { isAllowed } from '@/modules/document/components/file-info/actions/helpers'
import {
  resolveFailedAnalysisRetryWriteAccess,
  shouldShowFailedAnalysisRetryAction,
} from '@/modules/document/components/file-info/actions/failed-analysis-retry-action'
import {
  INVOICE_ANALYSIS_STATE,
  hasBookedAssignedExpense,
  isReanalysableNonInvoice,
  isRefreshableAnalysedInvoice,
  isReviewBlockedClassification,
} from '@/modules/document/utils/invoice-analysis-state'
import { isReviewableInvoiceArt } from '@/modules/document/utils/document-invoice-kind' // FLAG:useDocuAi
import TodoFormDrawer from '@/modules/todo/components/todo-form/todo-form-drawer'
import { DocumentAnalysisService } from '@/modules/document/services/documentAnalysis'
import { confirmAndRefreshInvoiceAnalysis } from '@/modules/document/services/refresh-invoice-analysis'
import { showResetInvoiceReviewError } from '@/modules/document/services/reset-invoice-review'
import {
  awaitableConfirmationModal,
  AwaitableConfirmationModalIcons,
} from '@/modules/base/components/modal/_helpers/awaitableConfirmationModal'
import { useNavigate } from 'react-router'
import DocumentSignatureDrawer from '@/modules/document/components/signature-drawer/document-signature-drawer'

const { MIMETYPE_PDF, MIMETYPE_TEXT_XML } = mimeTypes

const useStyles = makeStyles((theme: ThemeInterface) => ({
  documentActionsButton: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'flex-start',
    gap: 10,
  },
  menuItem: {
    '& svg *': {
      fill: CssHelper.important(theme.black),
    },
  },
}))

const DocumentActionsButton = (props) => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const classes = useStyles(props)

  const document = useAppSelector((state) => state.document.drawer.document)
  const folderReadOnly = useAppSelector((state) => state.document.drawer.folderReadOnly)
  const receiptReviewAccess = useAppSelector((state) => state.document.drawer.receiptReviewAccess)
  const subscriptions = useAppSelector((state) => state.tenant.info.subscriptions)
  const subscriptionInfo = useAppSelector((state) => state.subscription.details)

  const [shareHubDrawerOpen, setShareHubDrawerOpen] = useState(false)
  const [signatureDrawerOpen, setSignatureDrawerOpen] = useState(false)
  const [todoDrawerOpen, setTodoDrawerOpen] = useState(false)

  const isPdf = document?.mimetype === MIMETYPE_PDF
  const isXmlOrPdf = document?.mimetype === MIMETYPE_PDF || document?.mimetype === MIMETYPE_TEXT_XML
  const isDesktop = document?.externalOwnerSystem === EXTERNAL_OWNER_SYSTEMS.desktop
  const isReport = document?.tags?.find(({ isSystem, tagType }) => isSystem && ([TAG_TYPES.INTERIM_REPORT, TAG_TYPES.COUNT_REPORT] as TagType[]).includes(tagType))
  const isAssigned = document?.isAssigned
  // FLAG:useDocuAi — the review row's state, read once for the reset entry and its confirmation
  // text: 'review' = still open („Zu prüfen"), 'complete'/'partial' = reviewed.
  const reviewStatus = document?.documentInvoiceDetails?.reviewStatus
  const isReviewedInvoice = reviewStatus === INVOICE_ANALYSIS_STATE.COMPLETE
    || reviewStatus === INVOICE_ANALYSIS_STATE.PARTIAL
  const isIncomingInvoice = isXmlOrPdf && document?.tags?.find(({ tagType }) => tagType === TAG_TYPES.INCOMING_INVOICE)
  const useDocuAiEnabled = orgamax.user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi)
  // FLAG:useDocuAi — a non-invoice terminal may replace the upload's automatic Art.
  // Keep the user's receipt intent actionable without admitting recognized credit notes.
  const hasReceiptIntent = useDocuAiEnabled && isXmlOrPdf && !!document?.metaData?.receiptUploadAction
  const hasNonInvoiceReceiptIntent = hasReceiptIntent && isReanalysableNonInvoice(document)
  const hasIncomingInvoiceActions = isIncomingInvoice || hasNonInvoiceReceiptIntent

  const hasExpenseUserRights = useCheckAccessRights(USER_RIGHTS.EXPENSES.WRITE)
  const hasRevenueRights = useCheckAccessRights(USER_RIGHTS.REVENUES.WRITE) && useCheckModuleAccess(SUBSCRIPTION_MODULES.REVENUES)
  const hasExpenses = useCheckModuleAccess(SUBSCRIPTION_MODULES.EXPENSES)
  const hasSignatureRights = useCheckModuleAccess(SUBSCRIPTION_MODULES.DOCUMENT_SIGNATURE, subscriptionInfo)
  const hasPaymentRights = useCheckModuleAccess(SUBSCRIPTION_MODULES.DOCUMENT_PAYMENT, subscriptionInfo)
  const hasExpenseRights = hasExpenseUserRights && hasExpenses
  // FLAG:useDocuAi — "may this user edit documents?": the right question for review entry points
  // (see canActOnInvoiceReview). The Documents MODULE itself does not need checking here — without
  // that module, the drawer containing these actions would not exist.
  const hasDocumentWriteRights = useCheckAccessRights(USER_RIGHTS.DOCUMENTS.WRITE)
  const isCollaborationView = useAppSelector((state) => state.document.list.currentCollaboration)
  const hasFinances = useMemo(() => subscriptions.some((s) => s.typeOfSubscription === ProductTypes.MAIN) && !isCollaborationView, [subscriptions])

  const showShareMenu = !includes([DocumentType.INVOICE, DocumentType.CANCELLATION], document.type) && isPdf
  const showSignature = hasSignatureRights && !isDesktop && !isAssigned && document?.mimetype === MIMETYPE_PDF && !isReport

  // FLAG:useDocuAi — a Finance-opened terminal review row is deliberately a read-only document.
  // The server may nevertheless grant exactly one object-scoped capability for that exact row:
  // retry the failed analysis. Feed that capability into the EXISTING retry predicate; never infer
  // it from the client's broad Expenses/Revenues rights. A normal writable Documents drawer keeps
  // its established Documents-WRITE gate.
  const retryWriteAllowed = resolveFailedAnalysisRetryWriteAccess({
    folderReadOnly,
    hasDocumentWriteRights,
    canRetryFailedAnalysis: receiptReviewAccess?.canRetryFailedAnalysis === true,
  })
  const canRetryFailedAnalysis = shouldShowFailedAnalysisRetryAction({
    useDocuAiEnabled: !!useDocuAiEnabled,
    hasDocumentWriteRights: retryWriteAllowed,
    isDesktop,
    isIncomingInvoice: !!isIncomingInvoice,
    isAllowedDocument: isAllowed(document),
    document,
  })
  const handleFailedAnalysisRetry = () => DocumentAnalysisService.analyze({ retryFailed: true, skipModal: true })

  if (isCollaborationView) {
    return null
  }

  if (folderReadOnly) {
    return canRetryFailedAnalysis
      ? (
        <div className={classes.documentActionsButton}>
          <ButtonIconSquare
            onClick={handleFailedAnalysisRetry}
            label={lang.generics.reAnalyze}
            icon={IconScan}
            variant={'outline'}
            dataQsId='documentActions-retryFailedAnalysis'
          />
        </div>
      )
      : null
  }

  const onNavigate = (url) => saveDocument(() => {
    navigate(url)
  })

  const setShared = (value) => {
    FileService.setShareHUBState(document.id, value).then(data => {
      const { meta: { isShared } } = data

      if (isShared) {
        copyLink()
      } else {
        toast.showToast({ message: lang.documents.sharingDisabled })
      }

      dispatch(updateDocument({ key: 'isShared', value: isShared }))
      updateSharedState(document.id, isShared)
    })
  }

  const copyLink = () => {
    navigator.clipboard.writeText(document.shareHubUrl)
    toast.showToast({ message: lang.documents.shareHUBLinkCopied })
  }

  // FLAG:useDocuAi — „In Prüfung zurücksetzen". Confirm first (the reviewer's corrections are
  // dropped and a draft expense is deleted), then let the server re-derive; afterwards the drawer
  // closes and the list reloads so the tile shows the yellow "Zu prüfen" state again.
  const handleResetReview = async() => {
    const confirmed = await awaitableConfirmationModal({
      isOpen: true,
      title: lang.documents.resetInvoiceReview.title,
      // There are three cases, not two: „(Entwurf)" is wrong as soon as the expense has status „Offen"
      // — and because the final review save registers immediately in bookingApi, that is the normal
      // case for every reviewed invoice. The reset also does NOT update accounting; that belongs in
      // the confirmation text.
      // A review still open („Zu prüfen") has nothing to demote — only values to restore, and
      // only unsaved input to lose. Say exactly that instead of promising a status change.
      description: document.isAssigned
        ? (hasBookedAssignedExpense(document)
          ? lang.documents.resetInvoiceReview.confirmDescriptionWithBookedExpense
          : lang.documents.resetInvoiceReview.confirmDescriptionWithExpense)
        : (isReviewedInvoice
          ? lang.documents.resetInvoiceReview.confirmDescription
          : lang.documents.resetInvoiceReview.confirmDescriptionUnreviewed),
      confirmLabel: lang.documents.resetInvoiceReview.confirm,
      cancelLabel: lang.documents.resetInvoiceReview.cancel,
      confirmIcon: AwaitableConfirmationModalIcons.checkIcon,
    })
    if (!confirmed) { return }

    try {
      await FileService.resetInvoiceReview(document.id)
      toast.showToast({ message: lang.documents.resetInvoiceReview.success })
      // Keep the drawer OPEN: the reset changes THIS document, and the reviewer wants to see the result
      // (values restored from the analysis, status back to „Zu prüfen"). Closing it here returned the
      // user to the tile view. Reload the document in the drawer instead — and refresh the list behind
      // it so the tile turns yellow.
      dispatch(fetchDocumentDetails({ documentId: document.id }))
      // …and explicitly reload the review component in the Details tab as well (DMS report 4c28c454).
      // `fetchDocumentDetails` refreshes only the drawer
      // record; the review fetches its own data through
      // fetchFileInfo and depends on [documentId, reloadNonce] — neither changes when the SAME document
      // is reset. Without this, it remained at the pre-reset state (reported as “does not reload
      // the line items”), and the next save silently wrote the stale values back into the form.
      dispatch(requestReviewReload())
      props.refreshList?.()
    } catch (error) {
      showResetInvoiceReviewError(error)
    }
  }

  // FLAG:useDocuAi — „Als Rechnung analysieren". The gap this closes: when DocuAI's
  // analysis SUCCEEDS but classifies the document as a non-invoice (a Telekom
  // „Mobilfunkreport" reads as Abrechnung), no invoice schema ever runs, so there is no
  // review, no reviewStatus and no result — and therefore not one of the entries below
  // appears. The user could set the document type to „Rechnungseingang" by hand and nothing in the
  // whole product would act on it. This is the entry that acts on it.
  //
  // Gated on the ART or a terminal non-invoice receipt upload — unlike the review entries, which
  // key off the persisted classification precisely so a re-tagged contract cannot conjure a
  // review. Here the document type or upload intent is the user's assertion, and this action exists
  // to test it. It unlocks no review by itself — it produces invoice data or it does not,
  // and the normal gates decide what happens next.
  //
  // `isAnalysedNonInvoice` is the honest precondition: the analysis TERMINATED without an
  // invoice. It excludes a still-running analysis, and `!reviewStatus` keeps this away from
  // any document that already has a review to lose.
  const handleReanalyzeAsInvoice = async() => {
    const confirmed = await awaitableConfirmationModal({
      isOpen: true,
      title: lang.documents.reanalyzeAsInvoice.title,
      description: lang.documents.reanalyzeAsInvoice.description,
      confirmLabel: lang.documents.reanalyzeAsInvoice.confirm,
      cancelLabel: lang.documents.reanalyzeAsInvoice.cancel,
      confirmIcon: AwaitableConfirmationModalIcons.checkIcon,
    })
    if (!confirmed) { return }

    const started = await DocumentAnalysisService.reanalyzeAsInvoice()
    if (started) {
      // Pull the fresh in-flight markers into the drawer and the list behind it, so the
      // AI badge starts blinking immediately instead of after the next unrelated refresh.
      dispatch(fetchDocumentDetails({ documentId: document.id }))
      props.refreshList?.()
    }
  }

  // FLAG:useDocuAi — „Erneut analysieren": a genuine new DocuAI run for an already recognized
  // receipt. The dialog must explain the difference from „In Prüfung zurücksetzen" — otherwise the
  // user looks for the new result afterwards and finds the old values. If no review exists yet,
  // this paragraph is omitted: the new values are used directly there.
  const handleRefreshAnalysis = async() => {
    const started = await confirmAndRefreshInvoiceAnalysis(document)
    if (started) {
      // Pull the fresh in-flight markers into the drawer and list immediately so the AI badge starts
      // blinking at once rather than after the next unrelated refresh.
      dispatch(fetchDocumentDetails({ documentId: document.id }))
      props.refreshList?.()
    }
  }

  const isFile = !document.invoiceId && !document.cancellationId

  const actions: CascadingContextMenuItem[] = isFile
    ? [{
      key: 'todo',
      icon: IconTodo,
      label: lang.documents.todo,
      onClick: () => setTodoDrawerOpen(true),
    }]
    : []

  const isOutgoingInvoice = isXmlOrPdf && document.tags?.find(({ tagType }) => tagType === TAG_TYPES.INVOICE)
  // FLAG:useDocuAi — REVIEW entry points check the reviewable document type (incoming invoice OR
  // cancellation invoice). Recovery/booking also accepts terminal non-invoice receipt intent. The
  // distinction is functional: „Überweisen" and "create expense/revenue" are booking actions
  // that a credit note must NOT trigger (F18), and „Als Rechnung analysieren" deliberately gates on
  // the incoming-invoice type or receipt intent asserted by the user. Review is a document action.
  const isReviewableArt = isXmlOrPdf && isReviewableInvoiceArt(document)

  // FLAG:useDocuAi — "review again": the entry point for re-opening an ALREADY reviewed invoice.
  // The review surface itself (/documents/review/:id) has no reviewStatus gate, so this is purely
  // the missing door — and it deliberately bypasses the drawer's Details tab, which redirects a
  // document with an assigned expense straight to that expense. The document keeps reviewStatus
  // 'complete': it does NOT reappear in the "Zu prüfen" list. flag-OFF adds no menu entry at all.
  // FLAG:useDocuAi — "Erneut analysieren" is NOT a real customer feature on the DocuAI path: the
  // analysis result is already stored and this entry re-runs the LEGACY pipeline, so flag-ON it
  // promises something that does not happen. Its slot is taken by "In Prüfung zurücksetzen" below,
  // which delivers what the entry was reached for: restoring the original analysis values.
  // Flag-OFF the entry stays verbatim — there the legacy analysis is the real thing.
  // A genuine re-analysis (a real new DocuAI run) now exists as its own entry —
  // „Als Rechnung analysieren" below — but only for the one case that needs it: an
  // analysis that finished and recognized NO invoice. It is not a general re-scan.
  if (!useDocuAiEnabled && !isAssigned && isIncomingInvoice && !isDesktop) {
    actions.unshift({
      key: 'analyze',
      icon: IconScan,
      label: document.metaData?.ocr ? lang.documents.scanAgainButtonLabel : lang.documents.scanButtonLabel,
      onClick: () => DocumentAnalysisService.analyze({ isReanalyze: !!document.metaData?.ocr, skipModal: true }),
    })
  }

  // FLAG:useDocuAi — terminal failures need a real DocuAI retry, not the legacy re-scan
  // action above and not the transient "Jetzt erneut versuchen" pull-forward. The DMS
  // remains authoritative for the one paid manual retry (`retryAllowed`); the pure gate
  // also keeps this away from active, assigned and already-reviewed documents.
  if (canRetryFailedAnalysis) {
    actions.unshift({
      key: 'retryFailedAnalysis',
      icon: IconScan,
      label: lang.generics.reAnalyze,
      onClick: handleFailedAnalysisRetry,
    })
  }

  const canReanalyzeAsInvoice = useDocuAiEnabled && hasDocumentWriteRights && !isDesktop
    && hasIncomingInvoiceActions && isAllowed(document)
    && isReanalysableNonInvoice(document)
  if (canReanalyzeAsInvoice) {
    actions.unshift({
      key: 'reanalyzeAsInvoice',
      icon: IconScan,
      label: lang.documents.reanalyzeAsInvoice.menuAction,
      onClick: handleReanalyzeAsInvoice,
    })
  }
  // FLAG:useDocuAi — „Erneut analysieren". Closes the gap left by all four entries above: a receipt
  // whose analysis SUCCESSFULLY recognized an invoice had no path to a fresh run — `analyze` is
  // flag-OFF-only, `retryFailedAnalysis` requires an error, `reanalyzeAsInvoice` requires "no
  // invoice detected", and `resetReview` only re-derives from the stored old result. DocuAI
  // improvements therefore had no retroactive effect.
  //
  // Deliberately NOT gated on `!reviewStatus`/`!isAssigned`, unlike „Als Rechnung analysieren":
  // this run overwrites nothing (server-side F3 guard); it only stores a fresh result alongside the
  // old one. Adopting it remains an explicit reviewer action.
  //
  // `isReviewableArt` rather than `isIncomingInvoice`: the question "is my stored analysis result
  // outdated?" concerns review, not booking — it applies equally to a cancellation invoice (see
  // the comment by isReviewableArt above).
  const canRefreshAnalysis = useDocuAiEnabled && hasDocumentWriteRights && !isDesktop
    && isReviewableArt && isAllowed(document)
    && isRefreshableAnalysedInvoice(document)
  if (canRefreshAnalysis) {
    actions.unshift({
      key: 'refreshAnalysis',
      icon: IconScan,
      label: lang.documents.refreshAnalysis.menuAction,
      onClick: handleRefreshAnalysis,
    })
  }
  // FLAG:useDocuAi — review is a DOCUMENT action, not an accounting action. Previously, both entry
  // points depended on `hasExpenseRights` (expense permission AND module), so a tenant with only the
  // Documents module saw NEITHER „Erneut prüfen" NOR „In Prüfung zurücksetzen", even though review
  // is exactly what the tenant may do. The gate therefore uses document write permission (server-side
  // equivalent: canReviewDocumentInvoice). Booking remains protected separately: the service
  // authorizes changes to the expense through canCreateExpenses, so document rights alone can never
  // change a booking.
  // A classification orgaMAX Web has no review for (contract) never offers these entry points either.
  // `isReviewableArt` reads the document type, which the user may change — the shared predicate reads
  // what DocuAI actually recognized, so re-tagging a contract cannot conjure a review for it.
  const canActOnInvoiceReview = useDocuAiEnabled && hasDocumentWriteRights && !isDesktop
    && isReviewableArt && isAllowed(document) && !isReviewBlockedClassification(document)
  // FLAG:useDocuAi — the drawer already hosts the review; keep only the explicit reset action.
  // Offered for EVERY existing review row, not only a completed one: a review still in 'review' has
  // nothing to demote, but it has values to restore — the server replaces the row from the stored
  // (by now possibly fresh) analysis and the Details tab reseeds. That is the explicit door the
  // reviewer reaches for when the open review does not show the result they expect (Meldung
  // 44e29660: „hier fehlt Prüfung zurücksetzen"). Not while a run is in flight: the stored contract
  // is the OLD one until the terminal arrives, and the tab reloads on its own then.
  const offersResetReview = canActOnInvoiceReview
    && (isReviewedInvoice || (!!reviewStatus && !document?.metaData?.analysisInProgress))
  if (offersResetReview) {
    // FLAG:useDocuAi — the opposite door: put the invoice BACK to "Zu prüfen" with the values the
    // DocuAI analysis originally produced (server-side re-derive + REPLACE) and re-derive the draft
    // expense from them. A booked expense blocks it — the API refuses and we say so.
    actions.push({
      key: 'resetReview',
      icon: IconReset,
      // „In Prüfung zurücksetzen" only where there is a status to fall back from; an open review
      // just gets its values reset.
      label: isReviewedInvoice
        ? lang.documents.resetInvoiceReview.menuAction
        : lang.documents.resetInvoiceReview.action,
      onClick: handleResetReview,
    })
  }

  if (!isDesktop && !isAssigned && hasPaymentRights && hasIncomingInvoiceActions && isAllowed(document, subscriptions, { omo: ['M', 'L', 'XL'] })) {
    actions.push(
      {
        key: 'qrPay',
        icon: IconQrPay,
        label: lang.generics.pay,
        onClick: () => dispatch(openPaymentDrawer()),
      },
    )
  }

  if (showSignature && isAllowed(document)) {
    actions.push(
      {
        key: 'signing',
        icon: IconSign,
        label: lang.documents.signDocumentLabel,
        onClick: () => saveDocument(() => setSignatureDrawerOpen(true)),
      },
    )
  }

  if (showShareMenu && isAllowed(document, subscriptions, { omo: ['S', 'M', 'L', 'XL'], mbo: ['M', 'L', 'XL'] })) {
    const menuItem: CascadingContextMenuItem = {
      key: 'share',
      icon: IconShare,
      label: lang.documents.shareHub,
      onClick: () => setShared(true),
    }

    if (document.isShared) {
      menuItem.children = [
        {
          key: 'unshare',
          icon: IconUnshare,
          label: lang.documents.disableSharing,
          onClick: () => setShared(false),
        },
        {
          key: 'copyLink',
          icon: IconCopyLink,
          label: lang.documents.copyShareHUBLink,
          onClick: copyLink,
        },
        {
          key: 'shareHubEmail',
          icon: IconEmail,
          label: lang.documents.email.menuItem,
          onClick: () => saveDocument(() => setShareHubDrawerOpen(true)),
        },
      ]
    }

    actions.push(menuItem)
  }

  if (hasFinances && !document.isAssigned && !isDesktop && (hasIncomingInvoiceActions || isOutgoingInvoice) && isAllowed(document)) {
    const menuItem: CascadingContextMenuItem = {
      key: 'finances',
      icon: IconBank,
      label: lang.menu.finances.title,
      onClick: () => { },
      children: [],
    }

    if (hasRevenueRights && isOutgoingInvoice) {
      menuItem.children.push({
        key: 'addRevenue',
        label: lang.banking.list.createRevenue,
        icon: IconAdd,
        onClick: () => onNavigate(`/revenue/new/file/${document.id}`),
      })
    }
    if (hasExpenseRights && hasIncomingInvoiceActions) {
      menuItem.children.push({
        key: 'addExpense',
        label: lang.banking.list.createOutput,
        icon: IconAdd,
        // FLAG:useDocuAi — a non-invoice receipt has no review data; enter its values manually.
        // Recognized invoices keep the central review; flag-OFF keeps expense-from-file.
        onClick: () => onNavigate(useDocuAiEnabled && !hasNonInvoiceReceiptIntent
          ? `/documents/review/${document.id}`
          : `/expense/new/file/${document.id}`),
      })
    }

    if (menuItem.children.length) {
      actions.push(menuItem)
    }
  }

  const onSaveTodo = (todo) => {
    const value = [...document.linkedTodoFiles, { todoId: todo.id, title: todo.title }]
    dispatch(updateDocument({ key: 'linkedTodoFiles', value }))
    setTodoDrawerOpen(false)
    dispatch(closeDocumentDrawer())
    props.refreshList?.()
  }

  const children = (<>
    <SendEmailDrawer open={shareHubDrawerOpen} onClose={() => setShareHubDrawerOpen(false)} customerId={document.customer?.id} file={document} />
    <DocumentSignatureDrawer open={signatureDrawerOpen} onClose={() => setSignatureDrawerOpen(false)} document={document} onSave={onSaveTodo} />
    <TodoFormDrawer document={document} open={todoDrawerOpen} headline={lang.todo.create}
      onClose={() => setTodoDrawerOpen(false)}
      onSave={onSaveTodo}
      minWidth={497}
      withTypeSelect
      withClientSelect
    />
  </>)

  if (!actions.length) {
    return <div></div>
  }

  if (actions.length === 1 && !actions[0].children) {
    return (
      <div className={classes.documentActionsButton}>
        {children}
        <ButtonIconSquare onClick={actions[0].onClick} label={actions[0].label} icon={actions[0].icon} variant={'outline'} />
      </div>
    )
  }

  const menuItems = actions.map((action) => ({ ...action, customClass: classes.menuItem }))

  return (
    <div className={classes.documentActionsButton}>
      {children}
      <CascadingMenuButton label={lang.generics.additionalFunctions} menuItems={menuItems} />
    </div>
  )
}

export default DocumentActionsButton
